/**
 * ConsentBanner — bottom-sticky consent affordance.
 *
 * Feb 2026 Lane E. Ships DARK.
 *
 * Render contract:
 *   * Renders ONLY when BOTH:
 *       - `isConsentGatingEnabled()` returns true (feature flag ON)
 *       - Current consent status is "unset" (user hasn't chosen yet)
 *   * When rendered, sticks to the BOTTOM of the viewport (not
 *     modal, not blocking). User continues browsing.
 *   * NO auto-dismiss. User must click Accept or Decline —
 *     matters for GDPR-style consent, where implicit acceptance
 *     from continued browsing is NOT valid consent.
 *   * Accept  -> writes granted, tries to maybe-init the pixel
 *     dynamically (so users don't need a page refresh), banner
 *     unmounts.
 *   * Decline -> writes denied, banner unmounts. Pixel does NOT
 *     load. First-party beacon continues (documented in
 *     `lib/consent.js` module header).
 *
 * Accessibility:
 *   * Role=region + aria-label so screen readers announce it.
 *   * Buttons are proper <button> elements (keyboard-navigable).
 *   * Focus lands on the "Accept" button after mount (soft focus
 *     move — no focus trap because the banner is non-blocking).
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  getConsent,
  isConsentGatingEnabled,
  setConsent,
} from "@/lib/consent";
import { maybeInitPixel } from "@/lib/metaPixel";

export default function ConsentBanner() {
  const [status, setStatus] = useState(() => getConsent().status);
  const acceptRef = useRef(null);

  const showBanner = isConsentGatingEnabled() && status === "unset";

  useEffect(() => {
    if (showBanner && acceptRef.current) {
      // Soft focus move — helps keyboard + screen-reader users
      // notice the banner without being trapped.
      acceptRef.current.focus();
    }
  }, [showBanner]);

  if (!showBanner) return null;

  const handleAccept = () => {
    setConsent("granted");
    // Dynamically init the pixel so users who accept don't have
    // to refresh to start seeing accurate PageView tracking.
    maybeInitPixel();
    setStatus("granted");
  };

  const handleDecline = () => {
    setConsent("denied");
    setStatus("denied");
  };

  return (
    <div
      role="region"
      aria-label="Cookie and tracking consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t p-4 shadow-lg sm:p-5"
      style={{
        background: "var(--pp-surface)",
        borderColor: "var(--pp-border)",
      }}
      data-testid="consent-banner"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p
          className="text-sm leading-relaxed"
          style={{ color: "var(--pp-text)" }}
          data-testid="consent-banner-copy"
        >
          We use a small ad-measurement pixel (Meta Pixel) to see whether
          our Instagram + Facebook ads are working. Our own visit
          counting stays cookieless either way.{" "}
          <Link
            to="/privacy"
            className="underline"
            style={{ color: "var(--pp-purple)" }}
            data-testid="consent-banner-privacy-link"
          >
            Read our Privacy notes
          </Link>
          .
        </p>
        <div className="flex gap-2 sm:shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleDecline}
            data-testid="consent-banner-decline"
          >
            Decline
          </Button>
          <Button
            type="button"
            ref={acceptRef}
            onClick={handleAccept}
            data-testid="consent-banner-accept"
            style={{
              background: "var(--pp-orange)",
              color: "var(--pp-cta-text)",
            }}
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
